/**
 * Edpies — BoardPracticeBrowser
 *
 * Replaces the single-page board window with a proper drill-down:
 *
 *   Practice Hub  ──>  GL Assessment  ──>  English / Maths / VR / NVR  ──>  Paper 1, 2, 3 …
 *
 * Same "Skill Builder" pattern, but every leaf paper is tagged with the
 * board context so attempts are saved under that board and the AssessmentFactory
 * launches into the correct mock with the right title.
 *
 * Routed as: /practice/board/:board/:stage?   (stage is only meaningful for
 * Sutton SET which splits into Stage 1 + Stage 2 sub-papers).
 */

import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  ArrowLeft, ChevronDown, FileText, Clock, Target,
} from 'lucide-react';
import SubjectThumbnail from '@/components/SubjectThumbnail';

const BOARD_META = {
  sutton: { label: 'Sutton SET', subtitle: 'Sutton Schools Examination Test', stages: { '1': 'Stage 1 — Standardised Maths + English', '2': 'Stage 2 — Reasoning + Creative writing' } },
  gl: { label: 'GL Assessment', subtitle: 'Used by 75% of UK grammar schools' },
  cem: { label: 'CEM', subtitle: 'Centre for Evaluation & Monitoring — Durham' },
  iseb: { label: 'Independent Schools (ISEB)', subtitle: 'Common Pre-Test & ISEB style' },
  aqa: { label: 'AQA GCSE', subtitle: 'Assessment & Qualifications Alliance' },
  edexcel: { label: 'Edexcel GCSE', subtitle: 'Pearson Edexcel GCSE papers' },
  ocr: { label: 'OCR GCSE', subtitle: 'Oxford, Cambridge & RSA exams' },
};

// Subjects per board × stage. Stage 1 of Sutton SET is standardised Maths +
// English only; Stage 2 adds Reasoning + Creative writing. The other 11+
// boards (GL / CEM / ISEB) include the full quartet. GCSE boards include
// Maths + English + Science.
const SUBJECTS_FOR = (board, stage) => {
  if (board === 'sutton') {
    // Stage 2 of the Sutton SET is a written creative-writing task only —
    // VR/NVR/Maths/English are all assessed in Stage 1. Don't surface them
    // on Stage 2 or students will see Maths content under a writing label.
    return stage === '2'
      ? ['creative_writing']
      : ['maths', 'english'];
  }
  if (board === 'aqa' || board === 'edexcel' || board === 'ocr') {
    return ['maths', 'english', 'science'];
  }
  return ['english', 'maths', 'verbal_reasoning', 'non_verbal_reasoning'];
};

// Each board × subject offers a 6-paper window so 11+ students can build
// stamina across multiple papers per subject. Papers map back to the same
// premade_mock catalogue but are surfaced as "GL · English · Paper 2" etc.
const PAPER_COUNT = 6;

const SUBJECT_LABEL = {
  maths: 'Maths',
  english: 'English',
  creative_writing: 'Creative Writing',
  verbal_reasoning: 'Verbal Reasoning',
  non_verbal_reasoning: 'Non-Verbal Reasoning',
  science: 'Science',
};

const BoardPracticeBrowser = () => {
  const navigate = useNavigate();
  const { board = 'gl', stage } = useParams();
  const meta = BOARD_META[board.toLowerCase()] || { label: board.toUpperCase(), subtitle: '' };
  const subjects = useMemo(() => SUBJECTS_FOR(board.toLowerCase(), stage), [board, stage]);
  const [openSubject, setOpenSubject] = useState(null);

  const openPaper = (subject, paperNum) => {
    // Creative Writing isn't a multiple-choice mock — route to the
    // AI Writing Coach so students get the proper open-ended prompt + AI
    // feedback rather than Maths quiz questions.
    if (subject === 'creative_writing') {
      const params = new URLSearchParams();
      params.set('board', board);
      if (stage) params.set('stage', stage);
      params.set('prompt_id', `${board}-stage${stage || '1'}-${paperNum}`);
      navigate(`/writing-coach?${params.toString()}`);
      return;
    }

    // Every other subject routes into the existing Skill-Builder /papers
    // surface so each subject (English / Maths / VR / NVR / Science) opens
    // its OWN subject-specific paper bank — not the shared mock catalogue
    // that was returning Maths content for every subject.
    const isGcseBoard = ['aqa', 'edexcel', 'ocr'].includes(board.toLowerCase());
    const stageSlug = isGcseBoard ? 'gcse' : '11plus';
    const subjectSlug = subject.replace(/_/g, '-');

    // Pass returnTo so the PaperLibrary / PracticePaper "Back" can deep-link
    // the student straight back to the Board → Subject section instead of
    // dropping them on the generic mocks list.
    const returnTo = `/practice/board/${board}${stage ? `/${stage}` : ''}`;
    const params = new URLSearchParams();
    params.set('paper', String(paperNum));
    params.set('board', board);
    params.set('returnTo', returnTo);
    navigate(`/papers/${stageSlug}/${subjectSlug}?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-white dark:from-slate-950 dark:via-indigo-950 dark:to-slate-950">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-14 sm:pt-16 pb-12">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/practice-hub')}
          className="mb-4 text-slate-600 dark:text-slate-300"
          data-testid="board-browser-back"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Practice Hub
        </Button>

        {/* Banner */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mb-6"
        >
          <p className="text-xs uppercase tracking-wider font-semibold text-violet-700 dark:text-violet-300">
            Practice Hub · {meta.label}
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mt-1" data-testid="board-browser-title">
            {meta.label}{stage && meta.stages?.[stage] ? ` — Stage ${stage}` : ''}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1.5">
            {stage && meta.stages?.[stage] ? meta.stages[stage] : meta.subtitle}. Pick a subject to see its papers.
          </p>
        </motion.div>

        {/* Subject accordion → Paper grid */}
        <div className="space-y-3" data-testid="board-subjects">
          {subjects.map((subj) => {
            const isOpen = openSubject === subj;
            return (
              <Card
                key={subj}
                className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 overflow-hidden"
                data-testid={`board-subject-${subj}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenSubject(isOpen ? null : subj)}
                  className="w-full text-left p-4 flex items-center gap-3 hover:bg-slate-50/60 dark:hover:bg-slate-800/60 transition"
                  data-testid={`board-subject-${subj}-toggle`}
                >
                  <SubjectThumbnail subject={subj} size="md" animate={false} />
                  <div className="min-w-0 flex-1">
                    <h2 className="text-base font-bold text-slate-900 dark:text-white">
                      {SUBJECT_LABEL[subj] || subj}
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {PAPER_COUNT} {meta.label} papers
                    </p>
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="border-t border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-950/40"
                    >
                      <CardContent className="p-4">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                          {Array.from({ length: PAPER_COUNT }, (_, i) => i + 1).map((n) => (
                            <button
                              key={n}
                              type="button"
                              onClick={() => openPaper(subj, n)}
                              className="group p-3 rounded-lg bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 hover:border-violet-400 hover:shadow transition text-left"
                              data-testid={`board-${board}-${subj}-paper-${n}`}
                            >
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="h-8 w-8 rounded-md bg-violet-600 text-white text-sm font-bold flex items-center justify-center">
                                  {n}
                                </div>
                                <FileText className="h-4 w-4 text-slate-400 group-hover:text-violet-500 transition" />
                              </div>
                              <p className="text-sm font-semibold text-slate-900 dark:text-white">
                                Paper {n}
                              </p>
                              <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-1">
                                <span className="inline-flex items-center gap-0.5"><Clock className="h-3 w-3" />45m</span>
                                <span className="inline-flex items-center gap-0.5"><Target className="h-3 w-3" />25Q</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </CardContent>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BoardPracticeBrowser;
